// src/pages/inspector/InspectorDashboard.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InspectorLayout from '../../components/inspector/InspectorLayout';
import { useInspectorAuth } from '../../context/InspectorAuthContext';
import inspectorAPI from '../../services/inspectorAPI';
import styles from './InspectorDashboard.module.css';

const STATUS_COLOR = {
  pending: '#f59e0b',
  scheduled: '#3b82f6',
  in_progress: '#8b5cf6',
  completed: '#10b981',
  approved: '#059669',
  rejected: '#ef4444',
};

const STATUS_LABEL = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  approved: 'Approved',
  rejected: 'Rejected',
};

export default function InspectorDashboard() {
  const { inspector } = useInspectorAuth();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInspections();
  }, []);

  const loadInspections = async () => {
    try {
      const res = await inspectorAPI.getInspections();
      setInspections(res.data);
    } catch (err) {
      setError('Failed to load inspections. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Compute stats
  const stats = {
    total: inspections.length,
    today: inspections.filter((i) => i.preferred_date === new Date().toISOString().split('T')[0]).length,
    inProgress: inspections.filter((i) => i.status === 'in_progress').length,
    completed: inspections.filter((i) => ['completed', 'approved'].includes(i.status)).length,
    pending: inspections.filter((i) => i.status === 'pending').length,
    scheduled: inspections.filter((i) => i.status === 'scheduled').length,
  };

  // Upcoming inspections (next 7 days)
  const upcoming = inspections
    .filter((i) => ['pending', 'scheduled'].includes(i.status))
    .sort((a, b) => new Date(a.preferred_date) - new Date(b.preferred_date))
    .slice(0, 5);

  // Recent completed
  const recent = inspections
    .filter((i) => ['completed', 'approved'].includes(i.status))
    .slice(0, 5);

  return (
    <InspectorLayout>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              Good {getGreeting()}, {inspector?.name?.split(' ')[0] || 'Inspector'}!
            </h1>
            <p className={styles.subtitle}>
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <Link to="/inspector/inspections" className={styles.viewAllBtn}>
            View All →
          </Link>
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}

        {/* Stats Grid */}
        <div className={styles.statsGrid}>
          <StatCard label="Total Assigned" value={stats.total} icon="📋" color="#3b82f6" />
          <StatCard label="Today's" value={stats.today} icon="📅" color="#f59e0b" />
          <StatCard label="In Progress" value={stats.inProgress} icon="⚙️" color="#8b5cf6" />
          <StatCard label="Completed" value={stats.completed} icon="✅" color="#10b981" />
        </div>

        {/* Status Breakdown */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Status Overview</h2>
          <div className={styles.statusBar}>
            {Object.entries(STATUS_LABEL).map(([key, label]) => {
              const count = inspections.filter((i) => i.status === key).length;
              const pct = stats.total ? Math.round((count / stats.total) * 100) : 0;
              return (
                <div key={key} className={styles.statusBarItem}>
                  <div className={styles.statusBarHeader}>
                    <span className={styles.statusDot} style={{ background: STATUS_COLOR[key] }} />
                    <span className={styles.statusLabel}>{label}</span>
                    <span className={styles.statusCount}>{count}</span>
                  </div>
                  <div className={styles.progressBg}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${pct}%`, background: STATUS_COLOR[key] }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Two-column: Upcoming + Recent */}
        <div className={styles.twoCol}>
          {/* Upcoming */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Upcoming Inspections</h2>
            {loading ? (
              <div className={styles.loading}>Loading…</div>
            ) : upcoming.length === 0 ? (
              <EmptyState message="No upcoming inspections" />
            ) : (
              <div className={styles.list}>
                {upcoming.map((ins) => (
                  <InspectionRow key={ins.id} ins={ins} />
                ))}
              </div>
            )}
          </div>

          {/* Recent Completed */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Recently Completed</h2>
            {loading ? (
              <div className={styles.loading}>Loading…</div>
            ) : recent.length === 0 ? (
              <EmptyState message="No completed inspections yet" />
            ) : (
              <div className={styles.list}>
                {recent.map((ins) => (
                  <InspectionRow key={ins.id} ins={ins} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </InspectorLayout>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statIcon} style={{ background: `${color}18`, color }}>
        {icon}
      </div>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

function InspectionRow({ ins }) {
  return (
    <Link to={`/inspector/inspections/${ins.id}`} className={styles.inspRow}>
      <div className={styles.inspMeta}>
        <div className={styles.inspCar}>
          {ins.car.brand} {ins.car.model} <span className={styles.inspYear}>({ins.car.year})</span>
        </div>
        <div className={styles.inspRef}>{ins.booking_reference}</div>
      </div>
      <div className={styles.inspRight}>
        <StatusBadge status={ins.status} />
        <div className={styles.inspDate}>
          {new Date(ins.preferred_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </div>
      </div>
    </Link>
  );
}

export function StatusBadge({ status }) {
  return (
    <span
      className={styles.badge}
      style={{
        background: `${STATUS_COLOR[status] || '#94a3b8'}18`,
        color: STATUS_COLOR[status] || '#94a3b8',
        border: `1px solid ${STATUS_COLOR[status] || '#94a3b8'}40`,
      }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function EmptyState({ message }) {
  return (
    <div className={styles.emptyState}>
      <span>📭</span>
      <p>{message}</p>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}